import axios, { type InternalAxiosRequestConfig } from 'axios';
import { API_ROUTES, BASE_URL } from '../config/api-routes';
import {
  AUTH_EXPIRED_MESSAGE,
  clearAuthStorage,
  decodeTokenExpiryMs,
  getAccessToken,
  getRefreshToken,
  isAccessTokenExpired,
} from '../utils/authSession';
import {
  getUserFacingError,
  isRecoverableFetchError,
  notifyActionError,
} from '../utils/userFacingError';
import { useSessionStore } from '../store/sessionStore';
import { useUserStore } from '../store/userStore';

type AuthRetryConfig = InternalAxiosRequestConfig & {
  _authRetry?: boolean;
};

const axiosInstance = axios.create({
  baseURL: BASE_URL,
});

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let isLoggingOut = false;

export const startRefreshTimer = () => {
  const token = getAccessToken();
  if (!token) return;

  const expiryTime = decodeTokenExpiryMs(token);
  const now = Date.now();
  const refreshTime = expiryTime - now - 15 * 60 * 1000;

  if (refreshTime <= 0) {
    void refreshAccessToken();
    return;
  }

  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    void refreshAccessToken();
  }, refreshTime);
};

export const refreshAccessToken = async (): Promise<boolean> => {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      const refreshTokenValue = getRefreshToken();
      if (!refreshTokenValue) {
        return false;
      }

      const { data } = await axios.post(`${BASE_URL}${API_ROUTES.AUTH.REFRESH_TOKEN}`, {
        refreshToken: refreshTokenValue,
      });

      if (!data?.access_token) {
        return false;
      }

      localStorage.setItem('accessToken', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }

      startRefreshTimer();
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
};

export const logoutUser = (message?: string) => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  if (message) {
    sessionStorage.setItem('authRedirectMessage', message);
  }

  clearAuthStorage();
  useSessionStore.getState().clearSession();
  useUserStore.getState().clearUser();
  window.location.href = '/';
};

export const handleUnauthorized = () => {
  logoutUser(AUTH_EXPIRED_MESSAGE);
};

if (getAccessToken()) {
  startRefreshTimer();
}

axiosInstance.interceptors.request.use(async (config) => {
  let token = getAccessToken();

  if (token && isAccessTokenExpired(token)) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      handleUnauthorized();
      return Promise.reject(new Error('Session expired'));
    }
    token = getAccessToken();
  } else if (token) {
    const expTime = decodeTokenExpiryMs(token);
    const now = Date.now();
    if (expTime - now < 3 * 60 * 1000) {
      await refreshAccessToken();
      token = getAccessToken();
    }
  }

  if (token) {
    config.headers['authorization'] = `Bearer ${token}`;
  }

  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config as AuthRetryConfig | undefined;

    if (status === 401 && originalRequest && !originalRequest._authRetry && getRefreshToken()) {
      originalRequest._authRetry = true;
      const refreshed = await refreshAccessToken();
      const token = getAccessToken();

      if (refreshed && token) {
        originalRequest.headers['authorization'] = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      }
    }

    if (status === 401 || status === 403) {
      handleUnauthorized();
      return Promise.reject(error);
    }

    if (isRecoverableFetchError(error)) {
      useSessionStore.setState({
        connectionMessage: getUserFacingError(error),
      });
    }

    const suppressToast = originalRequest?.suppressErrorToast || isRecoverableFetchError(error);
    if (!suppressToast) {
      notifyActionError(error);
    }

    return Promise.reject(error);
  }
);

export { getUserFacingError, notifyActionError } from '../utils/userFacingError';

export default axiosInstance;
